/**
 * v111 — Schede di Controllo Pre-Operativo HACCP
 * Multi-tipologia: preoperativa mensile, sanificazione, ricevimento merci, NC, infestanti.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ClipboardCheck, Plus, Download, Pencil, Trash2, Calendar, Save } from "lucide-react";
import { toast } from "sonner";

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const giorniNelMese = (mese, anno) => new Date(anno, mese, 0).getDate();

export default function SchedePreoperativeHaccpPage() {
  const { user } = useAuth();
  const [tipi, setTipi] = useState([]);
  const [schede, setSchede] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog create
  const [createOpen, setCreateOpen] = useState(false);
  const [newTipo, setNewTipo] = useState("preoperativa_mensile");
  const [newAzienda, setNewAzienda] = useState("");
  const [newUL, setNewUL] = useState("");
  const [newMese, setNewMese] = useState(new Date().getMonth() + 1);
  const [newAnno, setNewAnno] = useState(new Date().getFullYear());
  const [newResp, setNewResp] = useState("");

  // Dialog edit (table)
  const [editScheda, setEditScheda] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, s, a, p] = await Promise.all([
        api.get("/schede-preoperative/tipi"),
        api.get("/schede-preoperative"),
        api.get("/aziende").catch(() => ({ data: [] })),
        api.get("/pdv").catch(() => ({ data: [] })),
      ]);
      setTipi(t.data || []);
      setSchede(s.data || []);
      setAziende(a.data || []);
      setPdvList(p.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento schede");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const tipoMeta = useMemo(
    () => tipi.find((t) => t.key === newTipo) || tipi[0],
    [tipi, newTipo]
  );

  const ulFiltered = useMemo(() => {
    if (!newAzienda) return pdvList;
    return pdvList.filter((p) => (p.azienda_id || "") === newAzienda);
  }, [pdvList, newAzienda]);

  const handleCreate = async () => {
    if (!newTipo || !newUL || !newAzienda) {
      toast.error("Compila Tipologia, Azienda e UL");
      return;
    }
    try {
      const res = await api.post("/schede-preoperative", {
        tipo: newTipo,
        azienda_id: newAzienda,
        unita_locale_id: newUL,
        mese: newMese,
        anno: newAnno,
        responsabile_nome: newResp || "",
      });
      toast.success("Scheda creata");
      setCreateOpen(false);
      await fetchAll();
      // Apri direttamente la modifica
      const created = await api.get(`/schede-preoperative/${res.data.id}`);
      setEditScheda(created.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare scheda ${s.tipo_label} ${MESI[s.mese - 1]} ${s.anno}?`)) return;
    try {
      await api.delete(`/schede-preoperative/${s.id}`);
      toast.success("Eliminata");
      await fetchAll();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const handleDownload = async (s) => {
    try {
      const resp = await api.get(`/schede-preoperative/${s.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.tipo}_${MESI[s.mese - 1]}_${s.anno}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="schede-preoperative-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-emerald-600" />
            Schede di Controllo Pre-Operativo
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Schede di autocontrollo HACCP mensili con auto-fill dei dati anagrafici dell'UL.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="new-scheda-btn"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuova Scheda
        </Button>
      </div>

      {/* Tipologie disponibili */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tipologie disponibili</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tipi.map((t) => (
              <div
                key={t.key}
                className="border rounded-lg p-3 hover:border-emerald-400 transition-colors"
                data-testid={`tipo-card-${t.key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{t.nome}</span>
                  <Badge variant={t.template_source === "custom" ? "default" : "outline"} className="text-[10px]">
                    {t.template_source === "custom" ? "Template custom" : t.template_source === "default" ? "Template base" : "Layout auto"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t.descrizione}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lista schede */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Schede esistenti</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Caricamento...</p>
          ) : schede.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna scheda creata. Clicca "Nuova Scheda" per iniziare.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Azienda / UL</TableHead>
                  <TableHead>Periodo</TableHead>
                  <TableHead>Responsabile</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schede.map((s) => (
                  <TableRow key={s.id} data-testid={`scheda-row-${s.id}`}>
                    <TableCell className="font-medium">{s.tipo_label}</TableCell>
                    <TableCell className="text-sm">
                      <div>{s.azienda_nome || "—"}</div>
                      <div className="text-muted-foreground text-xs">{s.unita_locale_nome || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{MESI[s.mese - 1]} {s.anno}</TableCell>
                    <TableCell className="text-sm">{s.responsabile_nome || "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditScheda(s)} data-testid={`edit-scheda-${s.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(s)} data-testid={`download-scheda-${s.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuova Scheda */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl" data-testid="create-scheda-dialog">
          <DialogHeader>
            <DialogTitle>Nuova Scheda di Controllo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Tipologia *</Label>
              <Select value={newTipo} onValueChange={setNewTipo}>
                <SelectTrigger data-testid="tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipi.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tipoMeta && (
                <p className="text-xs text-muted-foreground mt-1">{tipoMeta.descrizione}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Azienda *</Label>
                <Select value={newAzienda} onValueChange={(v) => { setNewAzienda(v); setNewUL(""); }}>
                  <SelectTrigger data-testid="azienda-select"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>
                    {aziende.map((a) => <SelectItem key={a.id} value={a.id}>{a.denominazione}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unità Locale *</Label>
                <Select value={newUL} onValueChange={setNewUL} disabled={!newAzienda}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder={newAzienda ? "Seleziona UL" : "Prima seleziona azienda"} /></SelectTrigger>
                  <SelectContent>
                    {ulFiltered.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Mese</Label>
                <Select value={String(newMese)} onValueChange={(v) => setNewMese(Number(v))}>
                  <SelectTrigger data-testid="mese-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anno</Label>
                <Input type="number" min={2024} max={2100} value={newAnno} onChange={(e) => setNewAnno(Number(e.target.value))} data-testid="anno-input" />
              </div>
              <div>
                <Label>Responsabile</Label>
                <Input value={newResp} onChange={(e) => setNewResp(e.target.value)} placeholder="Nome cognome" data-testid="resp-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create-scheda">
              Crea e compila
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Edit (table compilation) */}
      {editScheda && (
        <SchedaEditor
          scheda={editScheda}
          tipi={tipi}
          onClose={() => { setEditScheda(null); fetchAll(); }}
        />
      )}
    </div>
  );
}


function SchedaEditor({ scheda, tipi, onClose }) {
  const meta = tipi.find((t) => t.key === scheda.tipo);
  const colonne = meta?.colonne || [];
  const [righe, setRighe] = useState(scheda.righe || []);
  const [responsabile, setResponsabile] = useState(scheda.responsabile_nome || "");
  const [saving, setSaving] = useState(false);
  const giorni = giorniNelMese(scheda.mese, scheda.anno);

  // Garantisci righe complete
  useEffect(() => {
    if (righe.length < giorni) {
      const padded = [...righe];
      for (let i = righe.length; i < giorni; i++) {
        padded.push({ giorno: i + 1, c1: null, c2: null, c3: null, c4: null, c5: null, note_nc: "", firma_responsabile: "" });
      }
      setRighe(padded);
    }
  }, [giorni]);

  const setCell = (idx, key, val) => {
    setRighe((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  const allConforme = () => {
    setRighe((prev) => prev.map((r) => ({ ...r, c1: "C", c2: "C", c3: "C", c4: "C", c5: "C" })));
    toast.success("Tutti i controlli impostati a Conforme");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/schede-preoperative/${scheda.id}`, {
        righe,
        responsabile_nome: responsabile,
      });
      toast.success("Scheda salvata");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    await handleSave();
    try {
      const resp = await api.get(`/schede-preoperative/${scheda.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${scheda.tipo}_${MESI[scheda.mese - 1]}_${scheda.anno}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col" data-testid="edit-scheda-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-600" />
            {meta?.nome} — {MESI[scheda.mese - 1]} {scheda.anno}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {scheda.azienda_nome} · {scheda.unita_locale_nome}
          </p>
        </DialogHeader>
        <div className="flex items-center gap-3 py-2">
          <div className="flex-1">
            <Label className="text-xs">Responsabile</Label>
            <Input value={responsabile} onChange={(e) => setResponsabile(e.target.value)} placeholder="Nome cognome" />
          </div>
          <Button variant="outline" onClick={allConforme} data-testid="all-conforme-btn">
            Tutti Conformi
          </Button>
        </div>
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-emerald-50 sticky top-0 z-10">
              <tr>
                <th className="border p-2 w-12">Data</th>
                {colonne.map((c) => (
                  <th key={c.key} className="border p-2 min-w-[110px]">{c.label}</th>
                ))}
                <th className="border p-2 min-w-[200px]">Gestione NC e Azioni Correttive</th>
                <th className="border p-2 min-w-[140px]">Firma responsabile</th>
              </tr>
            </thead>
            <tbody>
              {righe.slice(0, giorni).map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" data-testid={`row-${i + 1}`}>
                  <td className="border p-2 text-center font-semibold">{r.giorno}</td>
                  {colonne.map((c) => (
                    <td key={c.key} className="border p-1 text-center">
                      <div className="flex gap-1 justify-center">
                        {["C", "NC"].map((v) => (
                          <button
                            key={v}
                            onClick={() => setCell(i, c.key, r[c.key] === v ? null : v)}
                            data-testid={`cell-${i + 1}-${c.key}-${v}`}
                            className={`px-2 py-0.5 rounded text-xs font-bold ${
                              r[c.key] === v
                                ? v === "C"
                                  ? "bg-emerald-500 text-white"
                                  : "bg-red-500 text-white"
                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </td>
                  ))}
                  <td className="border p-1">
                    <Input
                      value={r.note_nc || ""}
                      onChange={(e) => setCell(i, "note_nc", e.target.value)}
                      className="h-7 text-xs"
                      placeholder="Eventi/azioni correttive"
                    />
                  </td>
                  <td className="border p-1">
                    <Input
                      value={r.firma_responsabile || ""}
                      onChange={(e) => setCell(i, "firma_responsabile", e.target.value)}
                      className="h-7 text-xs"
                      placeholder="firma"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={handleSave} disabled={saving} variant="secondary" data-testid="save-scheda-btn">
            <Save className="w-4 h-4 mr-2" /> Salva
          </Button>
          <Button onClick={handleDownload} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-scheda-btn">
            <Download className="w-4 h-4 mr-2" /> Salva e Scarica DOCX
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
